import React from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

/**
 * Building blocks for the one-page Profile (issue #86): grouped rows that
 * each open a sheet, and the controls those sheets share.
 */

export const Group = ({ title, note, footer, children }) => (
  <section className="grid min-w-0 gap-2">
    <div className="flex items-baseline justify-between gap-2 px-0.5">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h2>
      {note && <p className="truncate text-xs text-muted-foreground/70">{note}</p>}
    </div>
    <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
      {children}
    </div>
    {footer && (
      <div className="px-0.5 text-xs leading-snug text-muted-foreground">{footer}</div>
    )}
  </section>
);

const rowBase =
  "w-full items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30";

/** One line: icon, label, what's set now, chevron. */
export const SummaryRow = ({ Icon, label, value, muted = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(rowBase, "grid grid-cols-[1.25rem_auto_minmax(0,1fr)_1rem]")}
  >
    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
    <span className="whitespace-nowrap text-sm font-medium text-foreground">{label}</span>
    <span
      className={cn(
        "truncate text-right text-[13px]",
        muted ? "text-muted-foreground" : "text-foreground",
      )}
    >
      {value}
    </span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
  </button>
);

/** A row whose control sits at its end (a small toggle), not a sheet. */
export const ControlRow = ({ Icon, label, children }) => (
  <div className={cn(rowBase, "grid grid-cols-[1.25rem_minmax(0,1fr)_auto] hover:bg-transparent")}>
    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
    <span className="whitespace-nowrap text-sm font-medium text-foreground">{label}</span>
    {children}
  </div>
);

/** A saved thing (a place, a benchmark): name, a detail line, an optional tag. */
export const ItemRow = ({ Icon, title, detail, tag, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      rowBase,
      "grid grid-cols-[1.25rem_minmax(0,1fr)_auto_1rem] disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent",
    )}
  >
    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
    <span className="min-w-0">
      <span className="block truncate text-sm font-medium text-foreground">{title}</span>
      {detail && (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{detail}</span>
      )}
    </span>
    {tag ? <span>{tag}</span> : <span />}
    <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
  </button>
);

/**
 * Basic mode's per-screen escape hatch (#172): shows the options Basic tucks
 * away, for this visit only.
 */
export const MoreOptionsRow = ({ onClick, label = "More options" }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-center gap-1 rounded-xl px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
  >
    {label}
    <ChevronDown className="h-4 w-4" />
  </button>
);

export const AddRow = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(rowBase, "grid grid-cols-[1.25rem_minmax(0,1fr)] text-sm font-semibold text-primary")}
  >
    <Plus className="h-[18px] w-[18px]" />
    {label}
  </button>
);

export const Tag = ({ children, className }) => (
  <span
    className={cn(
      "whitespace-nowrap rounded-md border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-xs tabular-nums text-foreground",
      className,
    )}
  >
    {children}
  </span>
);

export const DefaultTag = () => (
  <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
    Default
  </span>
);

/** A labelled block inside a sheet. */
export const Field = ({ label, hint, htmlFor, aside, children }) => (
  <div className="grid min-w-0 gap-2">
    {label && (
      <div className="flex items-baseline justify-between gap-2">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="mb-0 text-sm font-semibold text-foreground">
            {label}
          </label>
        ) : (
          <span className="text-sm font-semibold">{label}</span>
        )}
        {aside && <span className="text-xs text-muted-foreground">{aside}</span>}
      </div>
    )}
    {children}
    {hint && <p className="text-xs leading-snug text-muted-foreground">{hint}</p>}
  </div>
);

export const ChoiceChip = ({ selected, onClick, children, className }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-2 text-[13px] transition-colors",
      selected
        ? "border-primary bg-primary/10 font-semibold text-primary"
        : "border-border bg-background text-foreground hover:border-primary/40",
      className,
    )}
  >
    {children}
  </button>
);

/** Every option visible, wrapped (never a sideways-scrolling row). */
export const ChipGrid = ({ options, value, onChange, columns, allowNone = false }) => (
  <div
    className={cn(
      columns ? "grid gap-1.5" : "flex flex-wrap gap-1.5",
      columns === 2 && "grid-cols-2",
      columns === 3 && "grid-cols-3",
    )}
  >
    {options.map((o) => (
      <ChoiceChip
        key={o.value}
        selected={value === o.value}
        onClick={() => onChange(allowNone && value === o.value ? null : o.value)}
        className={columns && "rounded-lg px-2.5 text-left leading-tight"}
      >
        {o.label}
      </ChoiceChip>
    ))}
  </div>
);

/** Stacked option cards with a one-line description each. */
export const OptionList = ({ options, value, onChange }) => (
  <div className="grid gap-1.5">
    {options.map((o) => {
      const selected = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={selected}
          onClick={() => onChange(o.value)}
          className={cn(
            "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
            selected ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40",
          )}
        >
          <span className="min-w-0">
            <span className={cn("block text-sm font-semibold", selected && "text-primary")}>
              {o.label}
            </span>
            {o.description && (
              <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                {o.description}
              </span>
            )}
          </span>
          {o.aside && (
            <span className="font-mono text-xs text-muted-foreground">{o.aside}</span>
          )}
        </button>
      );
    })}
  </div>
);

export const SwitchRow = ({ id, label, description, checked, onCheckedChange }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
    <label htmlFor={id} className="mb-0 min-w-0 text-foreground">
      <span className="block text-sm font-semibold">{label}</span>
      {description && (
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
          {description}
        </span>
      )}
    </label>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

/**
 * Delete (or Unlink, Forget: `action`) inside an edit sheet. The first tap
 * asks, and says what it changes, so nothing goes on a single mis-tap.
 */
export const DeleteConfirm = ({ name, consequence, confirming, onAsk, onCancel, onConfirm, busy, action = "Delete" }) =>
  confirming ? (
    <div className="grid gap-2.5 rounded-xl border border-destructive/40 bg-destructive/10 p-3">
      <p className="text-sm leading-snug">
        <span className="font-semibold">{action} {name}?</span> {consequence}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Keep it
        </Button>
        <Button type="button" variant="destructive" size="sm" onClick={onConfirm} disabled={busy}>
          {action}
        </Button>
      </div>
    </div>
  ) : (
    <div className="border-t border-border pt-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAsk}
        className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        {action}
      </Button>
    </div>
  );
